// Merging what we know about where a visitor is.
//
// Two producers write the same field at different speeds. `visitor:presence`
// carries the COUNTRY on every heartbeat — resolved from the local table,
// because a provider call is far too slow to hold a heartbeat open — and
// `visitor:geo` carries the CITY once the external lookup lands.
//
// The monitor used to fold presence in with a plain object spread:
//
//     identity: { ...existing.identity, ...event.visitor }
//
// which replaces `geo` WHOLESALE, because geo is an object and the spread is
// one level too high. So the city that `visitor:geo` had just filled in was
// destroyed by the visitor's very next heartbeat, seconds later — the monitor
// fell back to a bare country and stayed there, while the database held the
// city the whole time. (The widget had the identical bug in its own store; the
// shape is easy to write and impossible to see.)

import type { VisitorIdentity } from '@prakash-software/shared';

type Geo = NonNullable<VisitorIdentity['geo']>;

const GEO_FIELDS = [
  'country',
  'countryCode',
  'region',
  'city',
  'ip',
  'latitude',
  'longitude',
] as const;

/**
 * Fold `incoming` into `existing`, field by field.
 *
 * A field the incoming payload omits keeps whatever was already known — that
 * is the whole point, since presence knows the country and not the city.
 *
 * EXCEPT when the two disagree about the COUNTRY. An IP that now resolves
 * somewhere else is a different place, not a richer view of the same one, so
 * keeping the old city would render "Ahmedabad, United Arab Emirates" for a
 * visitor who got on a plane or moved onto a VPN. When the country changes the
 * incoming answer is taken as a unit — the same coherence rule the server
 * applies when a provider disagrees with the local table.
 */
export function mergeGeo(existing: Geo | undefined, incoming: Geo | undefined): Geo | undefined {
  if (!incoming) return existing;
  if (!existing) return incoming;

  const before = existing.countryCode;
  const after = incoming.countryCode;
  if (before && after && before !== after) return incoming;

  const merged: Geo = { ...existing };
  for (const key of GEO_FIELDS) {
    const value = incoming[key];
    // Only a value that says something overwrites one that already did.
    // `null`/`''`/`undefined` all mean "this producer had nothing to add".
    if (value !== undefined && value !== null && value !== '') {
      (merged as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

/**
 * Fold an incoming identity into the one on the row, merging `geo` rather than
 * replacing it. Every other field keeps last-write-wins, which is correct:
 * they are scalars the server re-asserts in full on each heartbeat.
 */
export function mergeIdentity(
  existing: VisitorIdentity,
  incoming: VisitorIdentity,
): VisitorIdentity {
  const geo = mergeGeo(existing.geo, incoming.geo);
  const merged: VisitorIdentity = { ...existing, ...incoming };
  if (geo) merged.geo = geo;
  else delete merged.geo;
  return merged;
}
